import { useCallback } from "react";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import {
  AddTeamMembersToTeamDocument,
  CreateTeamDocument,
  type CreateTeamInput,
  DeleteTeamDocument,
  RemoveTeamMembersFromTeamDocument,
  UpdateTeamDocument,
  type UpdateTeamInput,
  ValidateTeamKeyDocument,
  type TeamMembershipRole,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
// createTeam / updateTeam / deleteTeam are gated by a server-side
// @RequireAccess decorator that THROWS a FORBIDDEN_* error (rather than
// returning a PermissionDenied union), so a denial lands in the catch.
// The global error link in apollo-client.ts already toasts those, so the
// catch blocks only toast the generic failure for NON-forbidden errors.
import { useAnalytics, AnalyticsContext, SettingsEvent } from "#app/lib/analytics";

export function useTeamMutations() {
  const client = useApolloClient();
  const { track } = useAnalytics();
  const [createTeamMutation] = useMutation(CreateTeamDocument);
  const [updateTeamMutation] = useMutation(UpdateTeamDocument);
  const [deleteTeamMutation] = useMutation(DeleteTeamDocument);
  const [addTeamMembersMutation] = useMutation(AddTeamMembersToTeamDocument);
  const [removeTeamMembersMutation] = useMutation(RemoveTeamMembersFromTeamDocument);

  const createTeam = useCallback(
    async (input: CreateTeamInput) => {
      try {
        const { data } = await createTeamMutation({
          variables: { input },
          refetchQueries: ["TeamsListData"],
        });
        const result = data?.createTeam;
        if (result && "code" in result) {
          toastManager.add({ title: result.message ?? "Failed to create team", type: "error" });
          return null;
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Create, label: "team" });
        toastManager.add({ title: "Team created", type: "success" });
        return result ?? null;
      } catch (e) {
        toastMutationError(e, "Failed to create team");
        return null;
      }
    },
    [createTeamMutation, track]
  );

  const updateTeam = useCallback(
    async (teamId: string, input: UpdateTeamInput) => {
      try {
        const { data } = await updateTeamMutation({
          variables: { teamId, input },
        });
        const result = data?.updateTeam;
        if (result && "code" in result) {
          toastManager.add({ title: result.message ?? "Failed to update team", type: "error" });
          return null;
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Update, label: "team" });
        toastManager.add({ title: "Team updated", type: "success" });
        return result ?? null;
      } catch (e) {
        toastMutationError(e, "Failed to update team");
        return null;
      }
    },
    [updateTeamMutation, track]
  );

  const deleteTeam = useCallback(
    async (teamId: string) => {
      try {
        const { data } = await deleteTeamMutation({
          variables: { teamId },
          refetchQueries: ["TeamsListData"],
        });
        const result = data?.deleteTeam;
        if (result && "code" in result) {
          toastManager.add({ title: result.message ?? "Failed to delete team", type: "error" });
          return null;
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Delete, label: "team" });
        toastManager.add({ title: "Team deleted", type: "success" });
        return result ?? null;
      } catch (e) {
        toastMutationError(e, "Failed to delete team");
        return null;
      }
    },
    [deleteTeamMutation, track]
  );

  const addTeamMembers = useCallback(
    async (teamId: string, userIds: string[], role: TeamMembershipRole = "member") => {
      try {
        const { data } = await addTeamMembersMutation({
          variables: { teamId, userIds, role },
        });
        const result = data?.addTeamMembers;
        if (result && "code" in result) {
          toastManager.add({ title: result.message ?? "Failed to add members", type: "error" });
          return null;
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.AddMembers, label: "team", data: { count: userIds.length, role } });
        toastManager.add({ title: "Members added", type: "success" });
        return result ?? null;
      } catch (e) {
        toastMutationError(e, "Failed to add members");
        return null;
      }
    },
    [addTeamMembersMutation, track]
  );

  const removeTeamMembers = useCallback(
    async (teamId: string, userIds: string[]) => {
      try {
        const { data } = await removeTeamMembersMutation({
          variables: { teamId, userIds },
        });
        const result = data?.removeTeamMembers;
        if (result && "code" in result) {
          toastManager.add({ title: result.message ?? "Failed to remove members", type: "error" });
          return null;
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.RemoveMembers, label: "team", data: { count: userIds.length } });
        toastManager.add({ title: "Members removed", type: "success" });
        return result ?? null;
      } catch (e) {
        toastMutationError(e, "Failed to remove members");
        return null;
      }
    },
    [removeTeamMembersMutation, track]
  );

  /** Smart diff — only adds new members and removes deleted ones. */
  const updateTeamMembership = useCallback(
    async (
      teamId: string,
      currentUserIds: string[],
      nextUserIds: string[],
      role: TeamMembershipRole = "member"
    ) => {
      try {
        const currentSet = new Set(currentUserIds);
        const nextSet = new Set(nextUserIds);

        const toAdd = nextUserIds.filter((id) => !currentSet.has(id));
        const toRemove = currentUserIds.filter((id) => !nextSet.has(id));

        await Promise.all([
          ...(toAdd.length > 0
            ? [addTeamMembersMutation({ variables: { teamId, userIds: toAdd, role } })]
            : []),
          ...(toRemove.length > 0
            ? [removeTeamMembersMutation({ variables: { teamId, userIds: toRemove } })]
            : []),
        ]);
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.UpdateMembership, label: "team", data: { added: toAdd.length, removed: toRemove.length } });
        toastManager.add({ title: "Team membership updated", type: "success" });
      } catch (e) {
        toastMutationError(e, "Failed to update team membership");
      }
    },
    [addTeamMembersMutation, removeTeamMembersMutation, track]
  );

  /** True when the key is available (mirrors useWorkflowMutations). */
  const validateTeamKey = useCallback(
    async (key: string) => {
      const { data, error } = await client.query({
        query: ValidateTeamKeyDocument,
        variables: { key },
        fetchPolicy: "network-only",
      });
      // errorPolicy "all" resolves with `error` set instead of rejecting —
      // rethrow so the caller's catch path stays the failure path.
      if (!data) throw error ?? new Error("Failed to validate team key");
      return data.validateTeamKey;
    },
    [client],
  );

  return {
    createTeam,
    updateTeam,
    deleteTeam,
    addTeamMembers,
    removeTeamMembers,
    updateTeamMembership,
    validateTeamKey,
  };
}
